import { useEffect, useLayoutEffect, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Helmet } from 'react-helmet'
import { Toaster, resolveValue } from 'react-hot-toast'
import { useQueryClient } from 'react-query'
import { Navigate, Outlet, Route, Routes, useLocation, useNavigationType } from 'react-router-dom'
import { enableMapSet } from 'immer'
import Spinner from './components/atoms/Spinner'
import StyledToastContainer from './components/atoms/toast/StyledToastContainer'
import BillingSettingsLinkHandler from './components/billing/BillingSettingsLinkHandler'
import SubscriptionReturnHandler from './components/billing/SubscriptionReturnHandler'
import { CalendarContextProvider } from './components/calendar/CalendarContext'
import { toast } from './components/molecules/toast'
import Toast from './components/molecules/toast/Toast'
import FocusModeScreen from './components/screens/FocusModeScreen'
import InternalDashboardScreen from './components/screens/InternalDashboardScreen'
import MainScreen from './components/screens/MainScreen'
import { FOCUS_MODE_ROUTE } from './constants'
import AppContextProvider from './context/AppContextProvider'
import { useFeatureFlags } from './services/api/feature-flags.hooks'
import { useGetUserInfo } from './services/api/user-info.hooks'
import {
    DAILY_HOME_AUTOMATIC_REDIRECT_STATE,
    isDailyHomeAutomaticRedirect,
    rememberDailyHomeDestination,
} from './utils/dailyHome'
import { READ_ONLY_MESSAGE, onReadOnlyRejection } from './utils/readOnly'
import { TFeatureFlagKey } from './utils/types'

enableMapSet() // this allows immer to produce immutable maps and sets

const DailyHomeHistoryObserver = () => {
    const { key, pathname, state } = useLocation()
    const navigationType = useNavigationType()
    const isInitialRoute = useRef(true)

    useLayoutEffect(() => {
        if (isInitialRoute.current) {
            isInitialRoute.current = false
            return
        }

        // Router-owned fallback redirects carry an explicit marker so stale deep links cannot become
        // the user's home. A later Back/Forward visit is deliberate, so only ignore marked replaces.
        if (navigationType === 'REPLACE' && isDailyHomeAutomaticRedirect(state)) return

        // Skipping the initial location preserves direct links and ordinary reloads, while observing
        // the router here covers every deliberate control and browser-history transition.
        rememberDailyHomeDestination(pathname)
    }, [key, navigationType, pathname, state])

    return null
}

const SubscriptionGate = () => {
    const { isLoading } = useGetUserInfo()
    const queryClient = useQueryClient()

    // An expired trial leaves the app readable; blocked writes get one shared toast, and a server
    // rejection means our user_info is stale, so refetch it to show the read-only bar.
    useEffect(
        () =>
            onReadOnlyRejection(() => {
                toast(READ_ONLY_MESSAGE, { toastId: 'read-only' })
                queryClient.invalidateQueries('user_info')
            }),
        [queryClient]
    )

    if (isLoading) {
        return <Spinner />
    }
    return <Outlet />
}

const FeatureGate = ({ flag }: { flag: TFeatureFlagKey }) => {
    const { data: featureFlags, isLoading } = useFeatureFlags()
    if (isLoading) return <Spinner />
    if (!featureFlags?.[flag]) return <Navigate to="/overview" replace state={DAILY_HOME_AUTOMATIC_REDIRECT_STATE} />
    return <Outlet />
}

const AuthenticatedRoutes = () => {
    return (
        <>
            <Helmet>
                <title>General Task</title>
            </Helmet>
            {/* {isDevelopmentMode && <ReactQueryDevtools initialIsOpen={false} position="top-left" />} */}
            <DndProvider backend={HTML5Backend}>
                <SubscriptionReturnHandler />
                <DailyHomeHistoryObserver />
                <AppContextProvider>
                    <BillingSettingsLinkHandler />
                    <Routes>
                        <Route element={<SubscriptionGate />}>
                            <Route path="overview" element={<Outlet />}>
                                <Route index element={<MainScreen />} />
                                <Route path=":overviewViewId" element={<MainScreen />}>
                                    <Route path=":overviewItemId" element={<MainScreen />}>
                                        <Route path=":subtaskId" element={<MainScreen />} />
                                    </Route>
                                </Route>
                            </Route>
                            <Route element={<FeatureGate flag="goals" />}>
                                <Route path="goals" element={<Outlet />}>
                                    <Route index element={<MainScreen />} />
                                    {/* Ahead of :goalId so "review" is never read as a goal id. */}
                                    <Route path="review" element={<MainScreen />} />
                                    <Route path="review/:goalId" element={<MainScreen />} />
                                    <Route path=":goalId" element={<MainScreen />} />
                                </Route>
                            </Route>
                            <Route element={<FeatureGate flag="routines" />}>
                                <Route path="routines" element={<Outlet />}>
                                    <Route index element={<MainScreen />} />
                                    <Route path=":routineId" element={<MainScreen />} />
                                </Route>
                            </Route>
                            <Route path="recurring-tasks" element={<Outlet />}>
                                <Route index element={<MainScreen />} />
                                <Route path=":recurringTaskId" element={<MainScreen />} />
                            </Route>
                            <Route path="tasks" element={<Outlet />}>
                                <Route index element={<MainScreen />} />
                                <Route path=":section" element={<MainScreen />}>
                                    <Route path=":task" element={<MainScreen />}>
                                        <Route path=":subtaskId" element={<MainScreen />} />
                                    </Route>
                                </Route>
                            </Route>
                            <Route path={FOCUS_MODE_ROUTE} element={<Outlet />}>
                                <Route
                                    index
                                    element={
                                        <CalendarContextProvider isPopoverDisabled={true}>
                                            <FocusModeScreen />
                                        </CalendarContextProvider>
                                    }
                                />
                            </Route>
                            <Route path="notes" element={<Outlet />}>
                                <Route index element={<MainScreen />} />
                                <Route path=":noteId" element={<MainScreen />} />
                            </Route>
                            <Route path="internal" element={<InternalDashboardScreen />} />
                        </Route>
                    </Routes>
                </AppContextProvider>
                <StyledToastContainer />
                <Toaster position="bottom-right" reverseOrder>
                    {(t) => <Toast toast={t}>{resolveValue(t.message, t)}</Toast>}
                </Toaster>
            </DndProvider>
        </>
    )
}

export default AuthenticatedRoutes
